import React, { cloneElement, isValidElement } from "react"
import SimpleElement from "../../Dashboard/ElementMapper/SimpleElement"
import { sanitizeProps, validateComponentType } from "../../Security/ConfigValidator"
import { SecurityMonitor } from "../../Security/XSSProtection"
import CompositeSectionsElement from "./CompositeSectionsElement"
import NotFoundElement from "./NotFoundElement"

// Secure component registry for pages
const secureComponentRegistry = new Map([
    ['Simple', SimpleElement],
    ['NotFound', NotFoundElement],
    ['CompositeSections', CompositeSectionsElement],
]);

const specToElementMapper = {
    Simple: <SimpleElement />,
    NotFound: <NotFoundElement />,
    CompositeSections: <CompositeSectionsElement />,
}

// Error Boundary for page components
class PageErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        SecurityMonitor.reportViolation(
            'COMPONENT_RENDER_ERROR',
            `Page component render error: ${error.message}`,
            {
                severity: 'HIGH',
                component: this.props.componentType || 'Unknown',
                error: error.toString(),
                stack: errorInfo.componentStack,
                timestamp: new Date().toISOString()
            }
        );
    }

    render() {
        if (this.state.hasError) {
            return <NotFoundElement message="Page temporarily unavailable" />;
        }
        return this.props.children;
    }
}

const getPagesElement = (spec, pagesCommons={}) => {
    try {
        // Validate component type
        if (!validateComponentType(spec.type, Array.from(secureComponentRegistry.keys()))) {
            SecurityMonitor.reportViolation(
                'UNAUTHORIZED_COMPONENT',
                `Unauthorized page component type: ${spec.type}`,
                {
                    severity: 'HIGH',
                    componentType: spec.type,
                    timestamp: new Date().toISOString()
                }
            );
            return <NotFoundElement message="Invalid page type" />;
        }

        // Sanitize props
        const sanitizedSpec = sanitizeProps(spec);

        // Get element from secure registry
        const element = specToElementMapper[sanitizedSpec.type];
        
        if (!element) {
            return <NotFoundElement message="Page not found" />;
        }

        // Wrap with performance monitoring and error boundary
        const pagesCommonsProps = pagesCommons;
        const header = pagesCommonsProps?.header;
        const footer = pagesCommonsProps?.footer;
        const headerComponent = getComponent(header);
        const footerComponent = getComponent(footer);
        const processed = isValidElement(element) ? cloneElement(element, sanitizedSpec) : element;
        
        return (
            <PageErrorBoundary componentType={sanitizedSpec.type}>
                {!sanitizedSpec.excludeHeader && headerComponent}
                {processed}
                {!sanitizedSpec.excludeFooter && footerComponent}
            </PageErrorBoundary>
        );
    } catch (error) {
        SecurityMonitor.reportViolation(
            'COMPONENT_PROCESSING_ERROR',
            `Error processing page component: ${error.message}`,
            {
                severity: 'HIGH',
                componentType: spec?.type || 'Unknown',
                error: error.toString(),
                timestamp: new Date().toISOString()
            }
        );
        
        return <NotFoundElement message="Page processing error" />;
    }
}

const getComponent = (spec) => {
    if (!spec) return null;
    if (!spec.type) return null;
    return getPagesElement(spec);
}

export default getPagesElement